import { useCustom } from '@refinedev/core';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { Rocket, Users, UserCheck, Mail, Activity, TrendingDown } from 'lucide-react';
import { formatNumber, formatDate } from '@/lib/utils';
import { HelpTooltip } from '@/components/ui/Tooltip';
import { StatCardSkeleton, Skeleton } from '@/components/ui/Skeleton';

// Wire-format mirrors backend/src/routes/admin.ts:LaunchCommandCenterStats.
// Kept inline (not imported from @udc/shared) because the admin frontend
// doesn't bundle the backend package; the contract is pinned by the
// source-presence lint at
// tests/unit/admin-launch-command-center-wiring-lint.test.ts.
interface LaunchCommandCenterStats {
  generated_at: string;
  operating_nsm: {
    weekly_new_match_ready: number;
    daily_trend: Array<{ date: string; count: number }>;
  };
  live: {
    touches_24h: number;
    signups_24h: number;
    match_ready_24h: number;
    waitlist_24h: number;
  };
  channels: {
    last_24h: Array<{ channel: string; count: number }>;
    last_7d: Array<{ channel: string; count: number }>;
  };
  recent_match_ready: Array<{
    user_id: string;
    became_match_ready_at: string;
    time_to_eligible_sec: number | null;
  }>;
}

// Wire-format mirrors backend GET /v1/admin/analytics/activation-funnel.
// Kept inline for the same reason as LaunchCommandCenterStats above — the
// admin frontend doesn't bundle the backend package.
interface ActivationFunnelStage {
  key: string;
  label: string;
  count: number | null;
  source: 'posthog' | 'db' | 'events';
  /** TRUE when the count is a FLOOR, not a total — see integrity below. */
  is_floor?: boolean;
  note?: string;
}
interface ActivationFunnelViolation {
  key: string;
  label: string;
  count: number;
  meaning: string;
}
/**
 * 🔴 `integrity` is not a debug aid — render it.
 *
 * The stages are INDEPENDENT predicates, not nested subsets. On PROD 2026-08-26
 * there are 3 users with a complete profile and an incomplete wizard, 1 with
 * hard filters and no profile, and 2 match-ready without onboarding. Read as a
 * ladder, those users are silently promoted into stages they never reached and
 * the totals still look plausible. When `monotone` is false a stage-to-stage
 * percentage is a RATIO, not a drop-off rate, and this page must not call it one.
 */
interface ActivationFunnelIntegrity {
  monotone: boolean;
  violations: ActivationFunnelViolation[];
  note: string;
}
interface ActivationFunnelPopulation {
  counted: number;
  live_all: number;
  excluded_internal: number;
  note: string;
}
interface ActivationFunnel {
  stages: ActivationFunnelStage[];
  population?: ActivationFunnelPopulation;
  integrity?: ActivationFunnelIntegrity;
  diagnostics?: {
    idv_failed?: { count: number; is_floor?: boolean; note?: string };
    onboarding_flag?: { count: number; note?: string };
  };
  installed_available: boolean;
  generated_at: string;
}

function StatTile({
  title,
  value,
  icon: Icon,
  tooltip,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  tooltip?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
            {title}
            {tooltip && <HelpTooltip content={tooltip} />}
          </p>
          <p className="text-2xl font-bold">{formatNumber(value)}</p>
        </div>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
          <Icon className="h-5 w-5 text-primary" />
        </div>
      </div>
    </div>
  );
}

function formatSecondsHuman(sec: number | null): string {
  if (sec == null) return '—';
  if (sec < 60) return `${sec}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
}

export function LaunchCommandCenterPage() {
  const { data, isLoading, isError } = useCustom<LaunchCommandCenterStats>({
    url: '/v1/admin/analytics/launch-command-center',
    method: 'get',
  });

  // isError is destructured DELIBERATELY. Without it a failed request leaves
  // isLoading false and funnel undefined, and the render below shows the
  // loading skeleton FOREVER — an operator waits on a spinner for a call that
  // already failed. A permanent "loading" is the UI form of an empty result
  // read as a clean one.
  const {
    data: funnelData,
    isLoading: funnelLoading,
    isError: funnelError,
  } = useCustom<ActivationFunnel>({
    url: '/v1/admin/analytics/activation-funnel',
    method: 'get',
  });

  const stats = data?.data;
  const funnel = funnelData?.data;

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive bg-destructive/10 p-4 text-destructive">
        Failed to load Launch Command Center. Please try again.
      </div>
    );
  }

  if (isLoading || !stats) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Rocket className="h-6 w-6" />
            Launch Command Center
          </h2>
          <p className="text-muted-foreground">
            Acquisition + activation health for the 2026-05-28 launch.
          </p>
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <StatCardSkeleton key={i} />
          ))}
        </div>
        <div className="rounded-lg border bg-card p-6">
          <Skeleton className="h-6 w-48 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold flex items-center gap-2">
          <Rocket className="h-6 w-6" />
          Launch Command Center
        </h2>
        <p className="text-muted-foreground">
          Acquisition + activation health for the 2026-05-28 launch. Channel
          attribution uses the canonical deriveChannel() function — values
          here always match what's stored on core.acquisition_touches.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Generated {formatDate(stats.generated_at)} · refresh by reloading
        </p>
      </div>

      <BetaActivationFunnel funnel={funnel} isLoading={funnelLoading} isError={funnelError} />

      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              Weekly New Match-Ready Users
              <HelpTooltip content="Operating north star: real users (is_fake=FALSE) who flipped to match-ready in the last 7 days. The 5-criteria gate fires became_match_ready_at when signup_wizard + profile + filters + verification + round-1 + active status all line up." />
            </h3>
            <p className="text-3xl font-bold text-primary">
              {formatNumber(stats.operating_nsm.weekly_new_match_ready)}
            </p>
          </div>
        </div>
        {stats.operating_nsm.daily_trend.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No match-ready flips in the last 30 days yet.
          </p>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.operating_nsm.daily_trend}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="date" fontSize={11} />
                <YAxis fontSize={11} allowDecimals={false} />
                <RechartsTooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '0.5rem',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-3">Last 24 hours</h3>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <StatTile
            title="Acquisition touches"
            value={stats.live.touches_24h}
            icon={Activity}
            tooltip="Rows landing in core.acquisition_touches via POST /v1/attribution. One per visitor session that hits the website with any UTM/click-ID."
          />
          <StatTile
            title="Signups"
            value={stats.live.signups_24h}
            icon={Users}
            tooltip="Real users (is_fake=FALSE) whose row was inserted into core.users_core in the last 24h."
          />
          <StatTile
            title="Match-ready flips"
            value={stats.live.match_ready_24h}
            icon={UserCheck}
            tooltip="Users who flipped to became_match_ready_at IS NOT NULL in the last 24h. This is the daily heartbeat of the operating NSM."
          />
          <StatTile
            title="Waitlist signups"
            value={stats.live.waitlist_24h}
            icon={Mail}
            tooltip="Leads captured via the ComingSoon page's POST /v1/waitlist endpoint. Carries UTM + click-IDs via PR-6 attribution capture."
          />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChannelCard
          title="Channel mix (24h)"
          channels={stats.channels.last_24h}
          tooltip="Acquisition touches grouped by deriveChannel() output: paid_search, paid_social, organic_search, organic_social, referral, direct, other."
        />
        <ChannelCard
          title="Channel mix (7d)"
          channels={stats.channels.last_7d}
          tooltip="Same grouping as the 24h column but with a 7-day window — useful for spotting per-channel trend deltas the 24h tile can't show."
        />
      </div>

      <div className="rounded-lg border bg-card p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          Recent match-ready cohort
          <HelpTooltip content="Last 10 real users to flip match-ready. time_to_eligible = duration from account-creation to the flip. A 5-second flip suggests a tester-bypass user; a multi-hour flip is the normal path." />
        </h3>
        {stats.recent_match_ready.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">
            No match-ready flips yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="py-2 font-medium">User</th>
                  <th className="py-2 font-medium">Flipped at</th>
                  <th className="py-2 font-medium">Time to eligible</th>
                </tr>
              </thead>
              <tbody>
                {stats.recent_match_ready.map((row) => (
                  <tr key={row.user_id} className="border-b last:border-0">
                    <td className="py-2">
                      <a
                        href={`/users/${row.user_id}`}
                        className="text-primary hover:underline font-mono text-xs"
                      >
                        {row.user_id.slice(0, 8)}…
                      </a>
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {formatDate(row.became_match_ready_at)}
                    </td>
                    <td className="py-2 font-mono">
                      {formatSecondsHuman(row.time_to_eligible_sec)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function formatConversion(ratio: number): string {
  return `${(ratio * 100).toFixed(ratio >= 0.1 ? 0 : 1)}%`;
}

function BetaActivationFunnel({
  funnel,
  isLoading,
  isError,
}: {
  funnel?: ActivationFunnel;
  isLoading: boolean;
  /** Load FAILED. Without this the component cannot tell "still loading" from
   *  "the request already failed", and renders a spinner forever. */
  isError?: boolean;
}) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <TrendingDown className="h-5 w-5 text-primary" />
        Beta Activation Funnel
      </h3>
      <p className="text-sm text-muted-foreground mt-1">
        Real testers (excludes fakes + deleted) progressing toward the North
        Star. Installed is PostHog-sourced (all environments); the rest are
        live DB state.
      </p>

      {isError || (!isLoading && !funnel) ? (
        <p className="mt-4 rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive">
          The activation funnel could not be loaded. This is an error, not an
          empty funnel — do not read it as “no members”.
        </p>
      ) : isLoading || !funnel ? (
        <div className="mt-4">
          <Skeleton className="h-24 w-full" />
        </div>
      ) : (
        <div className="mt-4 flex items-stretch gap-1 overflow-x-auto pb-2">
          {funnel.stages.map((stage, i) => {
            const prev = i > 0 ? funnel.stages[i - 1] : null;
            // A percentage between two stages is a DROP-OFF RATE only when the
            // later stage is a genuine subset of the earlier one. Two things
            // break that, and both are honoured here rather than papered over:
            //   - integrity.monotone === false: the population contains rows
            //     that satisfy a later stage without an earlier one;
            //   - either side is_floor: an events-backed count loses rows, so
            //     the ratio has an unknown error bar in a known direction.
            const laddered = funnel.integrity?.monotone !== false;
            const floored = prev?.is_floor === true || stage.is_floor === true;
            let conversion: React.ReactNode = null;
            if (prev) {
              if (prev.count != null && stage.count != null) {
                if (stage.count <= prev.count) {
                  const ratio = prev.count === 0 ? 0 : stage.count / prev.count;
                  const caveat = !laddered
                    ? ' — RATIO ONLY: the stages are not nested subsets (see the integrity notice), so this is not a drop-off rate.'
                    : floored
                      ? ' — one side is a floor (client-emitted events lose rows), so the true rate is no lower than this.'
                      : '';
                  conversion = (
                    <span
                      className={
                        laddered && !floored
                          ? 'text-xs font-semibold text-muted-foreground tabular-nums'
                          : 'text-xs font-semibold text-amber-700 dark:text-amber-500 tabular-nums'
                      }
                      title={`${stage.label} ÷ ${prev.label}${caveat}`}
                    >
                      {formatConversion(ratio)}
                      {(!laddered || floored) && <span aria-hidden="true">*</span>}
                    </span>
                  );
                } else {
                  // A later stage exceeding an earlier one. The tooltip used to
                  // blame ELIGIBILITY_OPEN unconditionally — but that flag is
                  // UNSET on PROD (verified against the live Lambda config), so
                  // on production it named a cause that cannot apply and sent a
                  // reader hunting a DEV artifact for a real inconsistency.
                  // The criteria are a CONJUNCTION, not a sequence, so a later
                  // stage legitimately exceeding an earlier one is ordinary.
                  conversion = (
                    <span
                      className="text-[10px] font-medium text-muted-foreground/70 leading-tight text-center"
                      title={`${stage.label} (${stage.count}) exceeds ${prev.label} (${prev.count}). The matching criteria are a conjunction, not a sequence, so this is not necessarily an error — see the integrity notice below. No drop-off rate is shown because these two stages are not nested.`}
                    >
                      ↑ exceeds prior
                    </span>
                  );
                }
              }
            }

            return (
              <div key={stage.key} className="flex items-stretch gap-1 shrink-0">
                {prev && (
                  <div className="flex w-12 flex-col items-center justify-center px-0.5">
                    {conversion}
                    <span className="mt-1 text-muted-foreground/40">→</span>
                  </div>
                )}
                <div
                  className="flex min-w-[7rem] flex-col items-center justify-center rounded-lg border bg-muted/30 p-3 text-center"
                  title={stage.note ?? undefined}
                >
                  <span className="text-2xl font-bold tabular-nums">
                    {stage.count == null ? '—' : formatNumber(stage.count)}
                  </span>
                  <span className="mt-1 flex items-center gap-1 text-xs font-medium text-muted-foreground">
                    {stage.label}
                    {stage.note && <HelpTooltip content={stage.note} />}
                  </span>
                  {stage.key === 'installed' &&
                    funnel.installed_available === false && (
                      <span className="mt-1 text-[10px] leading-tight text-muted-foreground/70">
                        Provision a PostHog query key to populate installs.
                      </span>
                    )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Denominator provenance. A funnel that silently counts staff accounts
          as members reports both a bigger population AND a bigger loss. */}
      {!isLoading && funnel?.population && funnel.population.excluded_internal > 0 && (
        <p className="mt-3 text-sm text-muted-foreground">
          Counting{' '}
          <strong className="tabular-nums text-foreground">
            {formatNumber(funnel.population.counted)}
          </strong>{' '}
          members —{' '}
          <strong className="tabular-nums">{funnel.population.excluded_internal}</strong> of{' '}
          {formatNumber(funnel.population.live_all)} live accounts are on our own domains and are
          excluded. <HelpTooltip content={funnel.population.note} />
        </p>
      )}

      {!isLoading && funnel?.diagnostics?.onboarding_flag != null && (
        <p className="mt-1 text-sm text-muted-foreground">
          <strong className="tabular-nums text-foreground">
            {formatNumber(funnel.diagnostics.onboarding_flag.count)}
          </strong>{' '}
          carry the Abby <code>onboarding_complete</code> flag — shown for contrast only.{' '}
          {funnel.diagnostics.onboarding_flag.note && (
            <HelpTooltip content={funnel.diagnostics.onboarding_flag.note} />
          )}
        </p>
      )}

      {/* The integrity notice. An endpoint that reports a caveat nobody renders
          has produced a field that is consumed by nothing — the same defect as
          a health flag emitted and never read. */}
      {!isLoading && funnel?.integrity && !funnel.integrity.monotone && (
        <div className="mt-3 rounded-md border border-amber-500/40 bg-amber-50/60 p-3 text-sm dark:bg-amber-950/20">
          <p className="font-semibold text-amber-900 dark:text-amber-300">
            These stages are not a ladder — the * rates above are ratios, not drop-offs.
          </p>
          <p className="mt-1 text-muted-foreground">{funnel.integrity.note}</p>
          <ul className="mt-2 space-y-1">
            {funnel.integrity.violations.map((v) => (
              <li key={v.key} className="text-muted-foreground">
                <strong className="tabular-nums text-foreground">{v.count}</strong> —{' '}
                {v.label}
                <span className="block text-xs text-muted-foreground/80">{v.meaning}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {!isLoading && funnel?.diagnostics?.idv_failed != null && (
        <p className="mt-3 text-sm text-muted-foreground">
          <strong className="tabular-nums text-foreground">
            {formatNumber(funnel.diagnostics.idv_failed.count)}
          </strong>{' '}
          distinct users have at least one failed ID verification.{' '}
          {funnel.diagnostics.idv_failed.note && (
            <HelpTooltip content={funnel.diagnostics.idv_failed.note} />
          )}
        </p>
      )}
    </div>
  );
}

function ChannelCard({
  title,
  channels,
  tooltip,
}: {
  title: string;
  channels: Array<{ channel: string; count: number }>;
  tooltip?: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-6">
      <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
        {title}
        {tooltip && <HelpTooltip content={tooltip} />}
      </h3>
      {channels.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">
          No touches in this window.
        </p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={channels} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis type="number" fontSize={11} allowDecimals={false} />
              <YAxis
                type="category"
                dataKey="channel"
                fontSize={11}
                width={110}
              />
              <RechartsTooltip
                contentStyle={{
                  backgroundColor: 'hsl(var(--card))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '0.5rem',
                }}
              />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
